import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AddressModel } from '../../../../../../shared/models/address.model';
import { PaymentGatewayModel } from '../../../../../../shared/models/payment-gateway.model';
import { CurrencyPipe } from '@angular/common';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { PaymentMethodModel } from '../../../../../../shared/models/payment-method.model';
import { PaymentStepsService } from '../../../../../services/payment-steps.service';
import { PaymentService } from '../../../../../services/payment.service';
import { CardModel } from '../../../../../../shared/models/payment/card.model';
import { PaymentGatewayNames } from '../../../../../../shared/enums/PaymentGatewayNames';

@Component({
    'selector': 'payment-payment-option-card-form',
    'templateUrl': './payment.payment.option.card.form.component.html',
    'providers': [CurrencyPipe],
    'styleUrls': ['../../../../../payment.component.scss']
})
export class PaymentPaymentOptionCardFormComponent implements OnInit {
    public differentBillingAddressFilled = false;
    public differentBillingAddress = false;
    public loading = false;
    public extraFieldsState = true;
    public mustBeTokenized: FormControl;
    public form: FormGroup;

    private cardValid = false;
    private extraFieldsData: Object;

    @Input() index: number;
    @Input() cardIndex: number;
    @Input() paymentMethod: PaymentMethodModel;
    @Input() paymentGateway: PaymentGatewayModel;
    @Input() deliverable: boolean;
    @Input() cardsCount: Array<number>;
    @Output() submitted: EventEmitter<AddressModel> = new EventEmitter();
    @Output() findingZipCode: EventEmitter<string> = new EventEmitter();
    @Output() foundZipCode: EventEmitter<string> = new EventEmitter();
    @Output() notFoundZipCode: EventEmitter<string> = new EventEmitter();
    @Output() filled: EventEmitter<CardModel> = new EventEmitter();
    @Output() unfilled: EventEmitter<any> = new EventEmitter();

    constructor(
        protected paymentStepsService: PaymentStepsService,
        protected paymentService: PaymentService
    ) {
        this.paymentStepsService.loadingInstallments$.subscribe(() => {
            this.loading = true;
        });
        this.paymentStepsService.loadedInstallments$.subscribe(() => {
            this.loading = false;
        });
    }

    ngOnInit() {
        this.mustBeTokenized = new FormControl(false, []);
        this.form = new FormGroup({
            must_be_tokenized: this.mustBeTokenized
        });

        if (!this.deliverable) {
            this.differentBillingAddress = true;
        }

        this.unfilled.emit(null);
    }

    /**
     * filled event listener.
     **/
    public onFilledAddress(address: AddressModel) {
        this.differentBillingAddressFilled = true;
        if (!this.deliverable) {
            this.paymentService.setBillingAddress(address);
        }

        this.checkFulfilment();
    }
    /**
     * unfilled event listener.
     **/
    public onUnfilledAddress() {
        this.differentBillingAddressFilled = false;
        this.checkFulfilment();
    }
    /**
     * submitted event listener.
     **/
    public onSubmitted(address: AddressModel): void {
        this.submitted.emit(address);
        this.paymentService.setBillingAddress(address);
    }
    /**
     * findingZipCode event listener.
     **/
    public onFindingZipCode(zipCode) {
        this.findingZipCode.emit(zipCode);
    }
    /**
     * foundZipCode event listener.
     **/
    public onFoundZipCode(zipCode) {
        this.foundZipCode.emit(zipCode);
    }
    /**
     * notFoundZipCode event listener.
     **/
    public onNotFoundZipCode(zipCode) {
        this.notFoundZipCode.emit(zipCode);
    }
    /**
     * Verify if card form was fulfilled.
     **/
    public checkFulfilment() {
        const extraFieldsValid = this.extraFieldsState;
        const billingAddressValid = (!this.differentBillingAddress || this.differentBillingAddressFilled);

        if (extraFieldsValid && billingAddressValid && this.cardValid) {
            this.setCardIntoPayment();
            this.filled.emit(this.form.getRawValue());
        } else {
            this.unfilled.emit(null);
        }
    }
    /**
     * Set selected card data into payment service.
     **/
    public setCardIntoPayment() {
        if (this.paymentMethod.allow_multiple_cards && this.cardsCount.length > 1) {
            this.paymentService.pushCard(this.cardIndex, this.form.getRawValue());
            return;
        }
        this.paymentService.setCard(this.form.getRawValue());
    }
    /**
     * Check fulfilment when card data is ready.
     **/
    public cardReady() {
        this.cardValid = true;
        this.checkFulfilment();
    }
    /**
     * Emit unfilled event when card is not ready.
     **/
    public cardPending() {
        this.cardValid = false;
        this.checkFulfilment();
    }
    /**
     * Set extraFieldsState property when changed
     **/
    public onExtraFieldsChange(isValid: boolean): void {
        this.extraFieldsState = isValid;
        this.checkFulfilment();
    }
    /**
     * Sets extra fields object to be sent for request
     **/
    public setExtraFieldsData(data: Object): void {
        this.extraFieldsData = data;
    }
    /**
     * Is default payment gateway if not is mercado pago.
     **/
    public isDefaultPaymentGateway() {
        return false === this.isMercadoPagoPaymentGateway()
            && false === this.isPagSeguroPaymentGateway()
            && false === this.isCieloPaymentGateway()
            && false === this.isBsPagPaymentGatewayWith3ds();
    }
    /**
     * Is default payment gateway if payment gateway tenant name is 'Mercado Pago'.
     **/
    public isMercadoPagoPaymentGateway() {
        return this.paymentGateway.name === PaymentGatewayNames.MERCADO_PAGO;
    }
    /**
     * Is default payment gateway if payment gateway tenant name is 'PagSeguro'.
     **/
    public isPagSeguroPaymentGateway() {
        return this.paymentGateway.name === PaymentGatewayNames.PAGSEGURO;
    }
    /**
     * Is cielo payment gateway if payment gateway tenant name is 'Cielo'.
     **/
    public isCieloPaymentGateway() {
        return this.paymentMethod.payment_gateway_tenant_name === PaymentGatewayNames.CIELO;
    }
    /**
     * Is bspag payment gateway if payment gateway tenant name is 'BsPag'.
     **/
    public isBsPagPaymentGateway() {
        return this.paymentMethod.payment_gateway_tenant_name === PaymentGatewayNames.BSPAG;
    }

    /**
     * Is 3ds allowed?.
     **/
    public is3dsAllowed() {
        return this.paymentMethod.allow_3ds;
    }

    public isBsPagPaymentGatewayWith3ds() {
        return this.isBsPagPaymentGateway() && this.is3dsAllowed();
    }

    public isCieloPaymentGatewayWith3ds() {
        return this.isCieloPaymentGateway() && this.is3dsAllowed();
    }
}



// WEBPACK FOOTER //
// /codebuild/output/src1143190010/src/src/app/payment/payment/option/card/option/form/payment.payment.option.card.form.component.ts